import type { Database } from "../../kernel/index.js";
import { getSetting, setSetting } from "../../kernel/index.js";

export interface ResolveUserOptions {
  /** If true, output JSON error instead of console.error (for bridge commands). */
  json?: boolean;
}

/**
 * Ensure the desktop has a usable local identity on first launch.
 * Existing explicit configuration always wins.
 */
export async function ensureDefaultUser(
  db: Database,
  preferredUserId?: string,
): Promise<string> {
  const stored = await getSetting(db, "user.id");
  if (stored) return stored;

  const userId =
    preferredUserId?.trim() ||
    process.env.ZAM_USER?.trim() ||
    process.env.USERNAME?.trim() ||
    process.env.USER?.trim() ||
    "default";
  await setSetting(db, "user.id", userId);
  return userId;
}

/**
 * Returns the user ID from the explicit --user flag, or falls back to the
 * stored `user.id` setting. Exits with an error if neither is available.
 */
export async function resolveUser(
  opts: { user?: string },
  db: Database,
  resolveOpts?: ResolveUserOptions,
): Promise<string> {
  if (opts.user) return opts.user;

  const stored = await getSetting(db, "user.id");
  if (stored) return stored;

  const message =
    "No user specified. Set a default with: zam whoami --set <id>";
  if (resolveOpts?.json) {
    console.log(JSON.stringify({ error: message }, null, 2));
  } else {
    console.error(message);
  }
  process.exit(1);
}
